/**
 * Scanning af en kvittering i browseren (beslutning 114, forbedret 30/9): find papiret, klip det ud, ret det op og
 * gør baggrunden hvid.
 *
 * <b>Hvorfor ikke OpenCV:</b> det er omkring 8 MB, som hver telefon skulle hente for én funktion. En kvittering er
 * et lyst, firkantet stykke papir - det kan findes med et par enkle trin:
 *
 * 1. Billedet skaleres ned og blødes lidt op.
 * 2. Der laves tre bud på, hvad der er papir:
 *    - det lyse og farveløse (den mindste af R, G og B er høj for hvidt papir, men lav for træ og farvede borde),
 *    - det lyse (gråtonen), begge delt med Otsus tærskel,
 *    - det, der er lukket inde af kanter (Sobel) - det virker også, når papiret ligger på et lyst bord.
 * 3. For hvert bud tages det største sammenhængende område, hullerne (teksten) fyldes, og der findes den største
 *    firkant med hjørner på områdets konvekse hylster. Budet, der ligner en firkant mest, vinder.
 * 4. Er firkanten tydelig (fylder området, nogenlunde rette vinkler, rører ikke billedets kant), klippes
 *    kvitteringen ud med det samme. Ellers vises hjørnerne, så de kan flyttes med fingeren.
 * 5. Så rettes billedet op med en perspektivtransformation, og gråtonerne strækkes, så papiret bliver hvidt og
 *    teksten sort.
 */

export interface Point {
  x: number
  y: number
}

/** Hjørnerne i rækkefølgen øverst til venstre, øverst til højre, nederst til højre, nederst til venstre. */
export type Quad = [Point, Point, Point, Point]

/** Hjørnerne, og om de er fundet sikkert nok til at klippe uden at spørge. */
export interface Detection {
  quad: Quad
  confident: boolean
}

const DETECT_EDGE = 480
const WORK_EDGE = 2200
const OUTPUT_LONG_EDGE = 1600

/** Indlæser billedet - skaleret ned, så en telefon med 48 MP ikke løber tør for hukommelse. */
export async function loadWorkImage(file: Blob): Promise<HTMLCanvasElement> {
  const bitmap = await createImageBitmap(file)
  try {
    const scale = Math.min(1, WORK_EDGE / Math.max(bitmap.width, bitmap.height))
    const canvas = document.createElement('canvas')
    canvas.width = Math.round(bitmap.width * scale)
    canvas.height = Math.round(bitmap.height * scale)
    canvas.getContext('2d')!.drawImage(bitmap, 0, 0, canvas.width, canvas.height)
    return canvas
  } finally {
    bitmap.close()
  }
}

/** Finder papirets fire hjørner. Rammer det ikke noget brugbart, foreslås et udsnit med lidt margen. */
export function detectDocument(source: HTMLCanvasElement): Detection {
  const scale = Math.min(1, DETECT_EDGE / Math.max(source.width, source.height))
  const w = Math.max(1, Math.round(source.width * scale))
  const h = Math.max(1, Math.round(source.height * scale))

  const small = document.createElement('canvas')
  small.width = w
  small.height = h
  const ctx = small.getContext('2d', { willReadFrequently: true })!
  ctx.drawImage(source, 0, 0, w, h)

  const found = findPaper(ctx.getImageData(0, 0, w, h).data, w, h)
  if (!found) return { quad: inset(source.width, source.height, 0.06), confident: false }

  return {
    quad: found.quad.map((p) => ({ x: p.x / scale, y: p.y / scale })) as Quad,
    confident: found.confident
  }
}

/** Et bud på papiret. `score` er 1 for en perfekt, fyldt firkant. */
export interface PaperCandidate {
  quad: Quad
  score: number
  confident: boolean
}

/**
 * Selve kantfindingen på rå RGBA-pixels - uden DOM, så den kan afprøves uden en browser. Svarer med det bedste
 * bud, eller null hvis intet ligner et stykke papir.
 */
export function findPaper(rgba: ArrayLike<number>, w: number, h: number): PaperCandidate | null {
  const n = w * h
  const lum = new Uint8Array(n)
  const white = new Uint8Array(n)
  for (let i = 0; i < n; i++) {
    const r = rgba[i * 4]
    const g = rgba[i * 4 + 1]
    const b = rgba[i * 4 + 2]
    lum[i] = (r * 299 + g * 587 + b * 114) / 1000
    white[i] = Math.min(r, g, b)
  }

  const blurredLum = boxBlur(lum, w, h)
  const candidates: PaperCandidate[] = []

  for (const channel of [boxBlur(white, w, h), blurredLum]) {
    const threshold = otsu(channel)
    const mask = new Uint8Array(n)
    for (let i = 0; i < n; i++) mask[i] = channel[i] > threshold ? 1 : 0
    // Åbning: skærer tynde broer over, så papiret ikke hænger sammen med en lys genstand ved siden af.
    const candidate = candidateFrom(dilate(erode(mask, w, h), w, h), w, h)
    if (candidate) candidates.push(candidate)
  }

  const enclosed = enclosedByEdges(blurredLum, w, h)
  const edgeCandidate = enclosed && candidateFrom(enclosed, w, h)
  if (edgeCandidate) candidates.push(edgeCandidate)

  if (candidates.length === 0) return null
  // Et sikkert bud vinder altid over et usikkert; ellers det, der ligner en firkant mest.
  candidates.sort((a, b) => Number(b.confident) - Number(a.confident) || b.score - a.score)
  return candidates[0]
}

/** Gør det største område i masken til et bud på papiret - eller null, hvis det ikke kan være et. */
function candidateFrom(mask: Uint8Array, w: number, h: number): PaperCandidate | null {
  const component = largestComponent(mask, w, h)
  if (!component) return null

  const filled = fillHoles(component.pixels, w, h)
  const imageArea = w * h
  // Under 8 % af billedet er for lidt; over 97 % er bordet, ikke papiret.
  if (filled.size < imageArea * 0.08 || filled.size > imageArea * 0.97) return null

  const hull = convexHull(rowExtremes(filled.mask, w, h))
  if (hull.length < 4) return null

  const quad = largestQuad(hull)
  const quadArea = area(quad)
  const hullArea = polygonArea(hull)
  if (quadArea < imageArea * 0.05 || hullArea <= 0) return null

  // Hvor godt firkanten dækker hylsteret, og hvor godt området fylder hylsteret (krøllet eller flosset = lavere).
  const score = Math.min(1, quadArea / hullArea) * Math.min(1, filled.size / hullArea)
  const confident =
    score >= 0.88 &&
    borderShare(filled.mask, w, h) < 0.04 &&
    quadArea < imageArea * 0.92 &&
    anglesWithin(quad, 50, 130)

  return { quad, score, confident }
}

/**
 * Det, der er lukket inde af kanter: kanterne (Sobel) findes og gøres lidt tykkere, så huller i omridset lukkes.
 * Alt, hvad baggrunden ude fra billedets kant ikke kan nå, er genstande - også hvidt papir på et lyst bord.
 */
function enclosedByEdges(gray: Uint8Array, w: number, h: number): Uint8Array | null {
  if (w < 3 || h < 3) return null
  const n = w * h
  const magnitude = new Uint8Array(n)
  for (let y = 1; y < h - 1; y++) {
    for (let x = 1; x < w - 1; x++) {
      const i = y * w + x
      const gx =
        gray[i - w + 1] + 2 * gray[i + 1] + gray[i + w + 1] - gray[i - w - 1] - 2 * gray[i - 1] - gray[i + w - 1]
      const gy =
        gray[i + w - 1] + 2 * gray[i + w] + gray[i + w + 1] - gray[i - w - 1] - 2 * gray[i - w] - gray[i - w + 1]
      magnitude[i] = Math.min(255, Math.hypot(gx, gy) / 4)
    }
  }

  // Mindst 12, så støj i et jævnt bord ikke tæller som kant.
  const threshold = Math.max(12, otsu(magnitude))
  const raw = new Uint8Array(n)
  for (let i = 0; i < n; i++) raw[i] = magnitude[i] > threshold ? 1 : 0
  const edges = dilate(raw, w, h)

  const outside = floodFromBorder(edges, w, h)
  const inside = new Uint8Array(n)
  for (let i = 0; i < n; i++) inside[i] = outside[i] ? 0 : 1
  // Kanten blev gjort én pixel tykkere - tag den af igen.
  return erode(inside, w, h)
}

/** Retter kvitteringen op og gør baggrunden hvid. Svarer med en JPEG, klar til at sende. */
export async function scanReceipt(source: HTMLCanvasElement, quad: Quad): Promise<Blob> {
  const [tl, tr, br, bl] = quad
  let outW = Math.max(dist(tl, tr), dist(bl, br))
  let outH = Math.max(dist(tl, bl), dist(tr, br))
  const scale = Math.min(1, OUTPUT_LONG_EDGE / Math.max(outW, outH))
  outW = Math.max(1, Math.round(outW * scale))
  outH = Math.max(1, Math.round(outH * scale))

  const src = source.getContext('2d', { willReadFrequently: true })!.getImageData(0, 0, source.width, source.height)
  const out = new ImageData(outW, outH)

  // Fra udsnittets hjørner (rektanglet) til papirets hjørner i det oprindelige billede.
  const H = homography(
    [
      { x: 0, y: 0 },
      { x: outW - 1, y: 0 },
      { x: outW - 1, y: outH - 1 },
      { x: 0, y: outH - 1 }
    ],
    quad
  )

  const gray = new Float32Array(outW * outH)
  for (let y = 0; y < outH; y++) {
    for (let x = 0; x < outW; x++) {
      const d = H[6] * x + H[7] * y + 1
      const sx = (H[0] * x + H[1] * y + H[2]) / d
      const sy = (H[3] * x + H[4] * y + H[5]) / d
      gray[y * outW + x] = sampleGray(src, sx, sy)
    }
  }

  whiten(gray, out)

  const canvas = document.createElement('canvas')
  canvas.width = outW
  canvas.height = outH
  canvas.getContext('2d')!.putImageData(out, 0, 0)

  return await new Promise<Blob>((resolve, reject) =>
    canvas.toBlob((blob) => (blob ? resolve(blob) : reject(new Error('Billedet kunne ikke gemmes.'))), 'image/jpeg', 0.85)
  )
}

// ---------------------------------------------------------------------------

/** Sætter 1 alle steder, som baggrunden (0 i `walls`) kan nå fra billedets kant. */
function floodFromBorder(walls: Uint8Array, w: number, h: number): Uint8Array {
  const reached = new Uint8Array(w * h)
  const stack = new Int32Array(w * h)
  let top = 0
  const push = (i: number) => {
    if (!walls[i] && !reached[i]) {
      reached[i] = 1
      stack[top++] = i
    }
  }
  for (let x = 0; x < w; x++) {
    push(x)
    push((h - 1) * w + x)
  }
  for (let y = 0; y < h; y++) {
    push(y * w)
    push(y * w + w - 1)
  }
  while (top > 0) {
    const i = stack[--top]
    const x = i % w
    if (x > 0) push(i - 1)
    if (x < w - 1) push(i + 1)
    if (i >= w) push(i - w)
    if (i < w * (h - 1)) push(i + w)
  }
  return reached
}

/** Området med hullerne (teksten, skygger inde på papiret) fyldt ud. */
function fillHoles(pixels: Int32Array, w: number, h: number): { mask: Uint8Array; size: number } {
  const component = new Uint8Array(w * h)
  for (const i of pixels) component[i] = 1
  const outside = floodFromBorder(component, w, h)
  const mask = new Uint8Array(w * h)
  let size = 0
  for (let i = 0; i < w * h; i++) {
    if (!outside[i]) {
      mask[i] = 1
      size++
    }
  }
  return { mask, size }
}

/** Andelen af billedets kantpixels, som området rører. */
function borderShare(mask: Uint8Array, w: number, h: number): number {
  let hits = 0
  for (let x = 0; x < w; x++) hits += mask[x] + mask[(h - 1) * w + x]
  for (let y = 1; y < h - 1; y++) hits += mask[y * w] + mask[y * w + w - 1]
  return hits / (2 * w + 2 * (h - 2))
}

/** Krymper 1-tallerne med én pixel (3 x 3). */
function erode(mask: Uint8Array, w: number, h: number): Uint8Array {
  const out = new Uint8Array(w * h)
  for (let y = 1; y < h - 1; y++) {
    for (let x = 1; x < w - 1; x++) {
      const i = y * w + x
      out[i] =
        mask[i] & mask[i - 1] & mask[i + 1] & mask[i - w] & mask[i + w] &
        mask[i - w - 1] & mask[i - w + 1] & mask[i + w - 1] & mask[i + w + 1]
    }
  }
  return out
}

/** Vokser 1-tallerne med én pixel (3 x 3). */
function dilate(mask: Uint8Array, w: number, h: number): Uint8Array {
  const out = new Uint8Array(w * h)
  for (let y = 0; y < h; y++) {
    for (let x = 0; x < w; x++) {
      if (!mask[y * w + x]) continue
      for (let dy = -1; dy <= 1; dy++) {
        const yy = y + dy
        if (yy < 0 || yy >= h) continue
        for (let dx = -1; dx <= 1; dx++) {
          const xx = x + dx
          if (xx >= 0 && xx < w) out[yy * w + xx] = 1
        }
      }
    }
  }
  return out
}

/** Den yderste pixel i hver side af hver række - nok til at finde det konvekse hylster. */
function rowExtremes(mask: Uint8Array, w: number, h: number): Point[] {
  const points: Point[] = []
  for (let y = 0; y < h; y++) {
    let left = -1
    let right = -1
    for (let x = 0; x < w; x++) {
      if (mask[y * w + x]) {
        if (left < 0) left = x
        right = x
      }
    }
    if (left < 0) continue
    // Pixlens yderkant, så firkanten dækker hele pixlen.
    points.push({ x: left, y }, { x: right + 1, y }, { x: left, y: y + 1 }, { x: right + 1, y: y + 1 })
  }
  return points
}

/** Det konvekse hylster (Andrews monotone kæde). */
export function convexHull(points: Point[]): Point[] {
  const sorted = [...points].sort((a, b) => a.x - b.x || a.y - b.y)
  if (sorted.length < 3) return sorted
  const cross = (o: Point, a: Point, b: Point) => (a.x - o.x) * (b.y - o.y) - (a.y - o.y) * (b.x - o.x)

  const lower: Point[] = []
  for (const p of sorted) {
    while (lower.length >= 2 && cross(lower[lower.length - 2], lower[lower.length - 1], p) <= 0) lower.pop()
    lower.push(p)
  }
  const upper: Point[] = []
  for (let i = sorted.length - 1; i >= 0; i--) {
    const p = sorted[i]
    while (upper.length >= 2 && cross(upper[upper.length - 2], upper[upper.length - 1], p) <= 0) upper.pop()
    upper.push(p)
  }
  return lower.slice(0, -1).concat(upper.slice(0, -1))
}

/**
 * Den største firkant med hjørner på hylsteret. Starter med de punkter, der ligger længst mod hvert hjørne, og
 * flytter derefter ét hjørne ad gangen hen på det punkt, der giver størst areal, til intet bliver bedre.
 */
export function largestQuad(hull: Point[]): Quad {
  const m = hull.length
  const pick = (score: (p: Point) => number) => {
    let best = 0
    for (let i = 1; i < m; i++) if (score(hull[i]) > score(hull[best])) best = i
    return best
  }
  // Indeks i hylsteret, sorteret, så hjørnerne står i hylsterets rækkefølge.
  let idx = [pick((p) => -p.x - p.y), pick((p) => p.x - p.y), pick((p) => p.x + p.y), pick((p) => p.y - p.x)]
  idx = [...new Set(idx)].sort((a, b) => a - b)
  if (idx.length < 4) idx = [0, Math.floor(m / 4), Math.floor(m / 2), Math.floor((3 * m) / 4)]

  const areaOf = (ids: number[]) => polygonArea(ids.map((i) => hull[i]))
  let best = areaOf(idx)

  for (let round = 0; round < 8; round++) {
    let improved = false
    for (let k = 0; k < 4; k++) {
      // Hjørnet må kun flytte sig mellem sine to naboer på hylsteret, så firkanten aldrig krydser sig selv.
      const prev = idx[(k + 3) % 4]
      const next = idx[(k + 1) % 4]
      for (let j = (prev + 1) % m; j !== next; j = (j + 1) % m) {
        const trial = [...idx]
        trial[k] = j
        const a = areaOf(trial)
        if (a > best + 1e-9) {
          best = a
          idx = trial
          improved = true
        }
      }
    }
    if (!improved) break
  }

  return orderQuad(idx.map((i) => hull[i]))
}

/** Sorterer fire punkter som øverst til venstre, øverst til højre, nederst til højre, nederst til venstre. */
function orderQuad(points: Point[]): Quad {
  const cx = points.reduce((s, p) => s + p.x, 0) / 4
  const cy = points.reduce((s, p) => s + p.y, 0) / 4
  // Med uret på skærmen (y nedad) er det stigende vinkel.
  const sorted = [...points].sort((a, b) => Math.atan2(a.y - cy, a.x - cx) - Math.atan2(b.y - cy, b.x - cx))
  let first = 0
  for (let i = 1; i < 4; i++) if (sorted[i].x + sorted[i].y < sorted[first].x + sorted[first].y) first = i
  return [0, 1, 2, 3].map((i) => sorted[(first + i) % 4]) as Quad
}

/** Om alle firkantens indre vinkler ligger mellem `min` og `max` grader. */
function anglesWithin(q: Quad, min: number, max: number): boolean {
  for (let i = 0; i < 4; i++) {
    const p = q[i]
    const a = q[(i + 3) % 4]
    const b = q[(i + 1) % 4]
    const cos =
      ((a.x - p.x) * (b.x - p.x) + (a.y - p.y) * (b.y - p.y)) / (dist(a, p) * dist(b, p) || 1)
    const angle = (Math.acos(Math.max(-1, Math.min(1, cos))) * 180) / Math.PI
    if (angle < min || angle > max) return false
  }
  return true
}

function polygonArea(points: Point[]): number {
  let sum = 0
  for (let i = 0; i < points.length; i++) {
    const a = points[i]
    const b = points[(i + 1) % points.length]
    sum += a.x * b.y - b.x * a.y
  }
  return Math.abs(sum) / 2
}

function boxBlur(gray: Uint8Array, w: number, h: number): Uint8Array {
  const out = new Uint8Array(w * h)
  for (let y = 0; y < h; y++) {
    for (let x = 0; x < w; x++) {
      let sum = 0
      let n = 0
      for (let dy = -2; dy <= 2; dy++) {
        const yy = y + dy
        if (yy < 0 || yy >= h) continue
        for (let dx = -2; dx <= 2; dx++) {
          const xx = x + dx
          if (xx < 0 || xx >= w) continue
          sum += gray[yy * w + xx]
          n++
        }
      }
      out[y * w + x] = sum / n
    }
  }
  return out
}

/** Otsus tærskel: den grænse, der deler histogrammet bedst i to grupper. */
export function otsu(values: Uint8Array): number {
  const histogram = new Array<number>(256).fill(0)
  for (const v of values) histogram[v]++

  const total = values.length
  let sumAll = 0
  for (let i = 0; i < 256; i++) sumAll += i * histogram[i]

  let sumBack = 0
  let weightBack = 0
  let best = 0
  let threshold = 127

  for (let t = 0; t < 256; t++) {
    weightBack += histogram[t]
    if (weightBack === 0) continue
    const weightFore = total - weightBack
    if (weightFore === 0) break

    sumBack += t * histogram[t]
    const meanBack = sumBack / weightBack
    const meanFore = (sumAll - sumBack) / weightFore
    const between = weightBack * weightFore * (meanBack - meanFore) ** 2

    if (between > best) {
      best = between
      threshold = t
    }
  }

  return threshold
}

/** Det største sammenhængende område af 1-taller (4-naboer). */
function largestComponent(mask: Uint8Array, w: number, h: number): { size: number; pixels: Int32Array } | null {
  const label = new Int32Array(w * h)
  const stack = new Int32Array(w * h)
  let bestLabel = 0
  let bestSize = 0
  let current = 0

  for (let start = 0; start < w * h; start++) {
    if (!mask[start] || label[start]) continue
    current++
    let top = 0
    let size = 0
    stack[top++] = start
    label[start] = current

    while (top > 0) {
      const i = stack[--top]
      size++
      const x = i % w
      const y = (i - x) / w
      if (x > 0 && mask[i - 1] && !label[i - 1]) { label[i - 1] = current; stack[top++] = i - 1 }
      if (x < w - 1 && mask[i + 1] && !label[i + 1]) { label[i + 1] = current; stack[top++] = i + 1 }
      if (y > 0 && mask[i - w] && !label[i - w]) { label[i - w] = current; stack[top++] = i - w }
      if (y < h - 1 && mask[i + w] && !label[i + w]) { label[i + w] = current; stack[top++] = i + w }
    }

    if (size > bestSize) {
      bestSize = size
      bestLabel = current
    }
  }

  if (!bestLabel) return null

  const pixels = new Int32Array(bestSize)
  let n = 0
  for (let i = 0; i < w * h; i++) if (label[i] === bestLabel) pixels[n++] = i
  return { size: bestSize, pixels }
}

function inset(w: number, h: number, margin: number): Quad {
  const mx = w * margin
  const my = h * margin
  return [
    { x: mx, y: my },
    { x: w - mx, y: my },
    { x: w - mx, y: h - my },
    { x: mx, y: h - my }
  ]
}

function dist(a: Point, b: Point) {
  return Math.hypot(a.x - b.x, a.y - b.y)
}

/** Arealet af firkanten (skonøreformlen). */
export function area(q: Quad): number {
  return polygonArea(q)
}

/**
 * Homografien, der fører de fire punkter i `from` over i `to`. Løses som et lineært system med 8 ubekendte
 * (h33 = 1). Svarer med [h11, h12, h13, h21, h22, h23, h31, h32].
 */
export function homography(from: Point[], to: Point[]): number[] {
  const A: number[][] = []
  const b: number[] = []

  for (let i = 0; i < 4; i++) {
    const { x, y } = from[i]
    const { x: u, y: v } = to[i]
    A.push([x, y, 1, 0, 0, 0, -u * x, -u * y])
    b.push(u)
    A.push([0, 0, 0, x, y, 1, -v * x, -v * y])
    b.push(v)
  }

  return solve(A, b)
}

/** Gauss-elimination med delvis pivotering. */
function solve(A: number[][], b: number[]): number[] {
  const n = b.length
  const M = A.map((row, i) => [...row, b[i]])

  for (let col = 0; col < n; col++) {
    let pivot = col
    for (let r = col + 1; r < n; r++) if (Math.abs(M[r][col]) > Math.abs(M[pivot][col])) pivot = r
    ;[M[col], M[pivot]] = [M[pivot], M[col]]

    const p = M[col][col] || 1e-12
    for (let c = col; c <= n; c++) M[col][c] /= p

    for (let r = 0; r < n; r++) {
      if (r === col) continue
      const f = M[r][col]
      if (f === 0) continue
      for (let c = col; c <= n; c++) M[r][c] -= f * M[col][c]
    }
  }

  return M.map((row) => row[n])
}

/** Gråtonen i et punkt mellem pixels (bilineær). Uden for billedet er det hvidt. */
function sampleGray(img: ImageData, x: number, y: number): number {
  const { width: w, height: h, data } = img
  if (x < 0 || y < 0 || x > w - 1 || y > h - 1) return 255

  const x0 = Math.floor(x)
  const y0 = Math.floor(y)
  const x1 = Math.min(x0 + 1, w - 1)
  const y1 = Math.min(y0 + 1, h - 1)
  const fx = x - x0
  const fy = y - y0

  const g = (xx: number, yy: number) => {
    const i = (yy * w + xx) * 4
    return (data[i] * 299 + data[i + 1] * 587 + data[i + 2] * 114) / 1000
  }

  const top = g(x0, y0) * (1 - fx) + g(x1, y0) * fx
  const bottom = g(x0, y1) * (1 - fx) + g(x1, y1) * fx
  return top * (1 - fy) + bottom * fy
}

/**
 * Hvid baggrund og sort tekst: gråtonerne strækkes, så de lyseste 60 % (papiret) bliver hvide, og de mørkeste 2 %
 * (teksten) bliver sorte. Skygger på papiret forsvinder næsten helt; teksten bliver skarpere.
 */
function whiten(gray: Float32Array, out: ImageData) {
  const histogram = new Array<number>(256).fill(0)
  for (const v of gray) histogram[Math.max(0, Math.min(255, Math.round(v)))]++

  const percentile = (p: number) => {
    const target = gray.length * p
    let count = 0
    for (let i = 0; i < 256; i++) {
      count += histogram[i]
      if (count >= target) return i
    }
    return 255
  }

  const black = percentile(0.02)
  const white = Math.max(black + 20, percentile(0.4))
  const range = white - black

  for (let i = 0; i < gray.length; i++) {
    let v = (gray[i] - black) / range
    v = Math.max(0, Math.min(1, v))
    // Lidt gamma, så tynd skrift fra en kasseapparat-printer ikke forsvinder.
    const c = Math.round(Math.pow(v, 1.6) * 255)
    out.data[i * 4] = c
    out.data[i * 4 + 1] = c
    out.data[i * 4 + 2] = c
    out.data[i * 4 + 3] = 255
  }
}
